/* eslint-disable @next/next/no-before-interactive-script-outside-document */
/* eslint-disable @next/next/no-sync-scripts */
import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { defineNextPage } from '@/common/next';
import NotFound from '@/components/NotFound';
import { getEnv } from '@/common/agents';
import Script from 'next/script';
import { openVipModal } from '$/store/useVipModalStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import {
  buildAiToolVipModalTrackParams,
  type AiToolUploadFunc,
} from '@/pages/--roboneo/utils/aiToolUploadTrack';
import { getPseoHreflangAlternates } from '@/pages/--roboneo/utils/pseoHreflang';
import type { OverseaFeatureItem } from '@/apiSdk/api.type';
import { fetchOverseaMenus } from '@/apiSdk/serverRquest';
import SeoCopyRight from '@/components/SeoCopyRight';
import FeatureContext from '@/components/landing_page/index';
import { Footer } from '@/components/overseaHome/layout';
import LazyV0Slot from '@/components/landing_page/components/LazyV0Slot';
import LazyAgentInput from '@/components/landing_page/components/LazyAgentInput';

// 这些 hyphen 路径由 Essio/PSEO 平台配置承接，批量上传按钮来自平台下发的 HTML/JS。
const PSEO_AI_TOOL_FUNC_MAP: Record<string, AiToolUploadFunc> = {
  'ai-cutout': 'cut_out',
  'ai-enhance': 'change_to_hd',
  'ai-eraser': 'remove',
  'ai-watermark': 'remove_aimark',
};

// PSEO AI 工具落地页只保留版权信息，不展示完整海外官网 footer 链接区。
const HIDE_SEO_FOOTER_PATHS = new Set([
  'ai-cutout',
  'ai-enhance',
  'ai-eraser',
  'ai-watermark',
]);

// 海外 Header 菜单接口异常时仍展示官网 Header 的基础入口，不阻断 Essio 页面渲染。
const EMPTY_OVERSEA_HEADER_MENUS: Record<'product' | 'blog', OverseaFeatureItem[]> = {
  product: [],
  blog: [],
};

interface PageData {
  id: number;
  title: string;
  description: string;
  keywords: string;
  html: string;
  jsUrl: string;
  cssUrl: string;
  canonical: string;
  styles: Record<string, string | number>;
  structuredData: Record<string, string>;
  projectId: number;
  templateId: number;
  publishedAt: string;
  templateConfig: Partial<Record<'v0Thumail' | 'v0Video' | 'v0Seedance', string | boolean>>;
}

interface EssioRedirectRule {
  id: number;
  path: string;
  status: string;
  redirect: string;
  validity: 'PERMANENT' | 'TEMPORARY';
}

interface PseoLandingPageProps {
  pageData: PageData | null;
  pathKey: string;
  templateConfig: Record<string, any> | undefined;
  countryCode: string;
  overseaHeaderMenus: Record<'product' | 'blog', OverseaFeatureItem[]>;
}

/** Essio 动态挂载的业务组件 id 映射。 */
const LAZY_WIDGET_IDS: Record<string, string> = {
  AgentInput: 'RoboNeoAgentInputContainer',
};

/**
 * 判断 Essio 下发的 HTML 是否包含指定业务组件挂载点。
 */
function isShowLazyComponents(name: string) {
  const id = LAZY_WIDGET_IDS[name];
  const idEle = document.querySelector(`#${id}`);
  return !!id && !!idEle;
}

// 复用海外站点规则判断 Essio 页面应该使用国内 SEO Header 还是海外官网 Header。
function isOverseaCountryCode(countryCode?: string) {
  if (!countryCode) return false;
  const mainlandCode = ['CN', 'TW', 'HK', 'MO'];
  return !mainlandCode.includes(countryCode.toUpperCase());
}

// 将海外官网菜单接口返回值整理成 Header 组件需要的 Product/Blog 两组菜单。
function normalizeOverseaHeaderMenus(menus?: { list?: {
  category: 'Product' | 'Blog';
  list: OverseaFeatureItem[];
}[]; }) {
  const productMenus = menus?.list?.filter((item) => item.category === 'Product')[0];
  const blogMenus = menus?.list?.filter((item) => item.category === 'Blog')[0];
  return {
    product: productMenus?.list ?? [],
    blog: blogMenus?.list ?? [],
  };
}

// 请求 Essio 重定向配置，接口异常或未命中时不阻断原页面内容渲染。
const fetchRedirect = async (path: string) => {
  const env = getEnv().ENV;
  const xStatus = env === 'release' ? 'published' : 'preview';
  const url = `https://${process.env.NEXT_PUBLIC_ESSIO_DOMAIN}/visitor-api/v1/redirects/${path}`;
  const startTime = Date.now();

  console.log(
    'fetchEssioRedirect request',
    `url:${url}`,
    `path:${path}`,
    `env:${env}`,
    `xStatus:${xStatus}`,
  );

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'x-api-key': `${process.env.NEXT_PUBLIC_X_API_KEY}`,
        'x-status': xStatus,
      },
    });
    const duration = Date.now() - startTime;

    if (response.status === 404) {
      console.log(
        'fetchEssioRedirect not found',
        `status:${response.status}`,
        `duration:${duration}ms`,
        `path:${path}`,
      );
      return null;
    }

    if (!response.ok) {
      const text = await response.text();
      console.error(
        'fetchEssioRedirect error response',
        `status:${response.status}`,
        `duration:${duration}ms`,
        `path:${path}`,
        `body:${text}`,
      );
      return null;
    }

    const { data } = await response.json() as { data: EssioRedirectRule | undefined; };
    if (!data?.redirect) {
      console.log(
        'fetchEssioRedirect empty',
        `status:${response.status}`,
        `duration:${duration}ms`,
        `path:${path}`,
      );
      return null;
    }

    console.log(
      'fetchEssioRedirect success',
      `status:${response.status}`,
      `duration:${duration}ms`,
      `path:${path}`,
      `redirect:${data.redirect}`,
      `validity:${data.validity}`,
    );
    return data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(
      'fetchEssioRedirect error',
      `duration:${duration}ms`,
      `path:${path}`,
      error,
    );
    return null;
  }
};

// 兼容 Essio 返回完整 URL 或站内相对路径，本地 development 保留 /--roboneo 前缀。
function normalizeRedirectTarget(redirect: string) {
  if (!redirect.startsWith('/') || redirect.startsWith('/--roboneo/')) {
    return redirect;
  }
  return getEnv().ENV === 'development' ? `/--roboneo${redirect}` : redirect;
}

// Pages Router 中断 SSR 响应执行 Essio 平台配置的 301/302 跳转。
function redirectByEssioRule(ctx: any, redirectRule: EssioRedirectRule) {
  const targetUrl = normalizeRedirectTarget(redirectRule.redirect);
  const statusCode = redirectRule.validity === 'PERMANENT' ? 301 : 302;

  if (ctx.res) {
    ctx.res.writeHead(statusCode, { Location: targetUrl });
    ctx.res.end();
    return;
  }

  if (typeof window !== 'undefined') {
    window.location.replace(targetUrl);
  }
}

// 请求 Essio 页面数据，路径保持与页面路由一致，不做多语言前缀拼接。
const fetchData = async (path: string) => {
  const env = getEnv().ENV;
  const xStatus = env === 'release' ? 'published' : 'preview';
  const url = `https://${process.env.NEXT_PUBLIC_ESSIO_DOMAIN}/visitor-api/v1/pages/${path}`;
  const startTime = Date.now();

  console.log(
    'fetchEssioPageData request',
    `url:${url}`,
    `path:${path}`,
    `env:${env}`,
    `xStatus:${xStatus}`,
  );

  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'x-api-key': `${process.env.NEXT_PUBLIC_X_API_KEY}`,
        'x-status': xStatus,
      },
    });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      const text = await response.text();
      console.error(
        'fetchEssioPageData error response',
        `status:${response.status}`,
        `duration:${duration}ms`,
        `path:${path}`,
        `body:${text}`,
      );
      throw new Error(`HTTP ${response.status}: ${text}`);
    }

    const { data } = await response.json() as { data: PageData | undefined; };
    if (!data) {
      console.error(
        'fetchEssioPageData error empty data',
        `status:${response.status}`,
        `duration:${duration}ms`,
        `path:${path}`,
      );
      throw new Error('empty data');
    }

    console.log(
      'fetchEssioPageData success',
      `status:${response.status}`,
      `duration:${duration}ms`,
      `path:${path}`,
      `pageId:${data.id}`,
      `title:${data.title}`,
    );
    return data;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(
      'fetchEssioPageData error',
      `duration:${duration}ms`,
      `path:${path}`,
      error,
    );
    throw error;
  }
};

// 通过事件捕获拦截平台下发的上传区按钮：普通上传补登录态，批量上传保留会员拦截。
function usePseoUploadAreaGuard(pathKey: string) {
  useEffect(() => {
    const normalizedPathKey = pathKey.replace(/^\/+|\/+$/g, '');
    const pseoAiToolFunc = PSEO_AI_TOOL_FUNC_MAP[normalizedPathKey];
    if (!pseoAiToolFunc) return undefined;

    const handleClick = (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Element)) return;

      const uploadAreaButton = target.closest('.pseo__upload-area-btn');
      const batchButton = target.closest('.pseo__batch-btn');
      if (!uploadAreaButton) return;

      const officialStore = useOfficialStore.getState();

      /**
       * Essio 落地页非 VIP 上传入口唤起订阅弹窗时，上报落地页来源。
       */
      const openPseoVipModal = () => {
        openVipModal('func_lodingpage', undefined, buildAiToolVipModalTrackParams(pseoAiToolFunc));
      };

      // 必须在 capture 阶段阻断，否则 Essio 自带 click 处理可能已经触发文件选择。
      const stopOriginalUpload = () => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      };

      const openLogin = () => {
        officialStore.toLogin()
          .then(async () => {
            if (!useOfficialStore.getState().isUserLoggedIn()) return;
            await useOfficialStore.getState().loadSubscribeInfo();
            if (batchButton && !useOfficialStore.getState().userInfo.subscribe?.isValid) {
              openPseoVipModal();
            }
          })
          .catch(() => {});
      };

      if (!officialStore.isUserLoggedIn()) {
        stopOriginalUpload();
        openLogin();
        return;
      }

      if (batchButton) {
        const isVip = Boolean(officialStore.userInfo.subscribe?.isValid);
        if (isVip) return;

        stopOriginalUpload();
        openPseoVipModal();
      }
    };

    // Essio 内容是动态注入的，使用 document 级委托保证后加载的按钮也能被拦截。
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [pathKey]);
}

// 渲染 Essio/PSEO 落地页，并按路径兼容不同页面的 SEO footer 露出策略。
function PseoLandingPage({
  pageData,
  pathKey,
  templateConfig,
  countryCode,
  overseaHeaderMenus,
}: PseoLandingPageProps) {
  const v0Thumail = templateConfig?.v0Thumail ?? false;
  const v0Video = templateConfig?.v0Video ?? false;
  const v0Seedance = templateConfig?.v0Seedance ?? false;
  const normalizedPathKey = pathKey.replace(/^\/+|\/+$/g, '');
  const showOverseaFooter = !HIDE_SEO_FOOTER_PATHS.has(normalizedPathKey);
  const isStoreOversea = useOfficialStore((state) => state.isOversea());
  // SSR 首屏优先使用 countryCode；客户端跳转或 req 不存在时，用已初始化的 store 兜底。
  const headerVariant = (isOverseaCountryCode(countryCode) || isStoreOversea) ? 'overseaOfficial' : 'seo';
  // 带首屏视觉的 v0Thumail 页面保留官网透明 Header，普通白底 PSEO 页首屏加遮罩保证导航可读。
  const overseaHeaderAppearance = v0Thumail ? 'transparent' : 'masked';
  const hreflangAlternates = getPseoHreflangAlternates(pathKey, pageData?.canonical);
  console.log('v0Seedanceeee', v0Seedance);

  usePseoUploadAreaGuard(pathKey);

  const [renderList, setRenderList] = useState<string[]>([]);
  const onReady = () => {
    const list: string[] = [];
    Object.keys(LAZY_WIDGET_IDS).forEach((name: string) => {
      if (isShowLazyComponents(name)) {
        list.push(name);
      }
    });
    setRenderList(list);
  };

  if (pageData) {
    return (
      <div>
        <Head>
          <title>{pageData.title}</title>
          <meta name="keywords" content={pageData.keywords} />
          <meta name="description" content={pageData.description} />
          <link rel="canonical" href={pageData.canonical} />
          {hreflangAlternates.map(({ hrefLang, href }) => (
            <link
              key={hrefLang}
              rel="alternate"
              hrefLang={hrefLang}
              href={href}
            />
          ))}
          <link rel="stylesheet" href={pageData.cssUrl} />
        </Head>
        <Script
          type="application/ld+json"
          strategy="beforeInteractive"
          id="structured-data"
        >
          {JSON.stringify(pageData.structuredData)}
        </Script>
        <Script src="https://cdn.tailwindcss.com" strategy="beforeInteractive" />
        {/* <header style={{ background: '#0A0A0A' }} /> */}
        {/* <main style={{ background: '#0a0a0a', minHeight: '100vh' }}> */}
        <main style={{ background: '#fff' }}>
          <FeatureContext
            v0Thumail={v0Thumail || v0Seedance}
            hideHeader={v0Video}
            landingHeaderShowUserCardSystemSetting={v0Seedance}
            headerFixedTop={!v0Seedance}
            headerVariant={headerVariant}
            overseaHeaderAppearance={overseaHeaderAppearance}
            overseaHeaderMenus={overseaHeaderMenus}
          >
            {v0Video && <LazyV0Slot kind="video" />}
            {v0Thumail && <LazyV0Slot kind="image" />}
            {v0Seedance && <LazyV0Slot kind="seedance" />}

            {/* 基于id 渲染业务组件 */}
            {!!renderList.length && renderList.map((name) => (
              name === 'AgentInput' ? (
                <LazyAgentInput key={name} id={LAZY_WIDGET_IDS[name]} />
              ) : null
            ))}

          </FeatureContext>
          <div style={{
            minHeight: 'calc(100vh - 137px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          }}
          >
            <div
              id="essio-root"
              style={{ width: '100%', ...pageData.styles }}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: pageData.html }}
            />
          </div>
        </main>
        <Script
          src={pageData.jsUrl}
          strategy="beforeInteractive"
          onReady={onReady}
        />
        {showOverseaFooter ? (
          <Footer countryCode={countryCode} />
        ) : (
          <footer style={{ background: '#0a0a0a', borderTop: '1px solid rgba(17, 18, 20, 0.06)' }}>
            <SeoCopyRight />
          </footer>
        )}

      </div>
    );
  }
  // Essio 未配置 seedance 落地页时 fetch 失败或无 data，仍走独立 Seedance（与 seedance/index.page.tsx 一致）
  if (v0Seedance) {
    return (
      <>
        <main style={{ background: '#fff', minHeight: '100vh' }}>
          <FeatureContext
            v0Thumail
            hideHeader={false}
            landingHeaderShowUserCardSystemSetting
            headerFixedTop={false}
            headerVariant={headerVariant}
            overseaHeaderAppearance={overseaHeaderAppearance}
            overseaHeaderMenus={overseaHeaderMenus}
          >
            <LazyV0Slot kind="seedance" />
          </FeatureContext>
        </main>
        {showOverseaFooter ? (
          <Footer countryCode={countryCode} />
        ) : (
          <footer style={{ background: '#0a0a0a', borderTop: '1px solid rgba(17, 18, 20, 0.06)' }}>
            <SeoCopyRight />
          </footer>
        )}
      </>
    );
  }
  return <NotFound />;
}

export default defineNextPage<{
  pageData: PageData | null;
  pathKey: string | '';
  templateConfig: Record<string, any> | undefined;
  countryCode: string;
  overseaHeaderMenus: Record<'product' | 'blog', OverseaFeatureItem[]>;
}>({
  pageConfig: {
    projectId: '/--roboneo',
    layoutId: '',
    renderHead({ pageProps }) {
      return (
        <Head>
          <title>RoboNeo</title>
        </Head>
      );
    },
  },
  // Essio 页面服务端获取平台内容，同时预取海外官网 Header 菜单，保证海外首屏导航可 SSR 输出。
  async getInitialProps(ctx) {
    const withoutProject = (ctx.asPath || '').replace('/--roboneo/', '');
    const curPath = withoutProject.split('?')[0].replace(/^\//, '') || withoutProject.split('?')[0];
    console.log(
      'fetchEssioPageData context',
      `asPath:${ctx.asPath}`,
      `curPath:${curPath}`,
    );
    let pathData: PageData | null = null;
    let templateConfig = {};
    let overseaHeaderMenus = EMPTY_OVERSEA_HEADER_MENUS;
    const cookie = ctx.req?.headers.cookie;
    const countryCode = ((ctx.req as any)?.countryCode as string)
      || (typeof ctx.query.countryCode === 'string' ? ctx.query.countryCode : '');
    const requestOptions = { cookie, countryCode };
    try {
      const redirectRule = await fetchRedirect(curPath);
      if (redirectRule && redirectRule.redirect) {
        redirectByEssioRule(ctx, redirectRule);
        return {
          pageData: null,
          pathKey: curPath,
          templateConfig: {},
          countryCode,
          overseaHeaderMenus,
        };
      }

      const [essioPageData, overseaMenus] = await Promise.all([
        fetchData(curPath),
        // 菜单接口失败不应影响 PSEO 内容投放，失败时使用空菜单降级。
        fetchOverseaMenus(requestOptions).catch(() => null),
      ]);
      pathData = essioPageData;
      overseaHeaderMenus = normalizeOverseaHeaderMenus(overseaMenus || undefined);
      templateConfig = pathData?.templateConfig || {};
    } catch (e) {
      console.error(
        'fetchEssioPageData getInitialProps error',
        `curPath:${curPath}`,
        e,
      );
    }
    return {
      pageData: pathData,
      pathKey: curPath,
      templateConfig: {
        ...templateConfig,
      },
      countryCode,
      overseaHeaderMenus,
    };
  },
  component: PseoLandingPage,
});
